/**
 * src/utils/api.js
 * Typed fetch helpers for all Pages Function endpoints.
 * ADMIN_TOKEN is read from localStorage (set on login).
 */

const BASE = import.meta.env.VITE_API_BASE ?? "";

function adminHeaders(extra = {}) {
  const token = localStorage.getItem("adminToken") ?? "";
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
    ...extra,
  };
}

async function handle(res) {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error ?? `HTTP ${res.status}`);
  return data;
}

// ── Storefront endpoints ────────────────────────────────────

/** Public: list published storefronts */
export const listPublished = () =>
  fetch(`${BASE}/api/storefronts`).then(handle);

/** Admin: list all (draft + published) */
export const listAll = () =>
  fetch(`${BASE}/api/storefronts?admin=1`, { headers: adminHeaders() }).then(handle);

/** Admin: create storefront */
export const createStorefront = (body) =>
  fetch(`${BASE}/api/storefronts`, {
    method: "POST", headers: adminHeaders(), body: JSON.stringify(body),
  }).then(handle);

/** Admin: update storefront fields */
export const updateStorefront = (id, body) =>
  fetch(`${BASE}/api/storefronts/${id}`, {
    method: "PATCH", headers: adminHeaders(), body: JSON.stringify(body),
  }).then(handle);

/** Admin: delete storefront */
export const deleteStorefront = (id) =>
  fetch(`${BASE}/api/storefronts/${id}`, {
    method: "DELETE", headers: adminHeaders(),
  }).then(handle);

/** Admin: publish or unpublish */
export const setPublishState = (id, action) =>
  fetch(`${BASE}/api/storefronts/${id}/publish`, {
    method: "POST", headers: adminHeaders(), body: JSON.stringify({ action }),
  }).then(handle);

/** Public: resolve slug → storefront */
export const getBySlug = (slug) =>
  fetch(`${BASE}/api/slug/${slug}`).then(handle);

/** Admin: get single storefront (incl. draft) */
export const getStorefrontAdmin = (id) =>
  fetch(`${BASE}/api/storefronts/${id}`, { headers: adminHeaders() }).then(handle);

// ── Products endpoint ───────────────────────────────────────

/** Fetch products for a storefront (admin auth needed if draft) */
export const getProducts = (storefrontId, { bust = false, isAdmin = false } = {}) => {
  const url = `${BASE}/api/products?storefrontId=${storefrontId}${bust ? "&bust=1" : ""}`;
  return fetch(url, { headers: isAdmin ? adminHeaders() : {} }).then(handle);
};
